import React, { useState, FC, useEffect, useMemo, useCallback } from 'react';
import { ComponentRendering, Text } from '@sitecore-jss/sitecore-jss-react';
import { isEmptyResponse } from '../../../utils/checkEmptyResponse';
import { WithTranslation } from 'react-i18next';
import { TClickPageNavEvent, TFormTitleUrl, TNavigationBarProps, TTabsNavigationBar } from './types/navigationBar.types';
import { NavigationBarModel } from './models/navigationBar.model';
import { documentService, windowService } from '../../../utils/window.utils';
import { ANCHOR_COMPONENT, NAVIGATION_BAR_ANCHOR_CLASS } from '../../../constants/navigationBar.constants';
import { BreakpointsInPx } from '../../../constants/breakpoints.constants';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import { googleClickEvent } from '../../../utils/analytics';
import NavigationBarDropdownDesktop from './components/navigationBarDropdownDesktop';
import NavigationBarTabsDesktop from './components/navigationBarTabsDesktop';
import NavigationBarMSRPDesktop from './components/navigationBarMSRPDesktop';
import NavigationBarCtaDesktop from './components/navigationBarCtaDesktop';
import NavigationBoxMobile from './components/navigationBoxMobile';
import NavigationTabsMobile from './components/navigationTabsMobile';
import { disableScroll, enableScroll, isScrolling } from '../../../utils/scroll.utils';

const hiddenTabsBasedOnContentAvailability = (tabs, componentsJssMain, anchorComponents, isEditorial) => {
  return tabs?.map((tab, index) => {
    const tempTab = { ...tab };

    for (let i = 0; i < componentsJssMain.length; i++) {
      // try to find a ProductSeriesNavigationBarAnchorComponent
      if (componentsJssMain[i].componentName === ANCHOR_COMPONENT) {
        // if in the anchorComponent with index === current tab there is an uid === componentsJssMain.uid that means im in the right position
        if (anchorComponents[index].uid === componentsJssMain[i].uid) {
          // if i find one anchor component and then the next is another anchor component it means that i dont have any components to show between the anchor components so the tab must be hidden
          if (componentsJssMain[i].componentName === ANCHOR_COMPONENT && componentsJssMain[i + 1]?.componentName === ANCHOR_COMPONENT) {
            tempTab.isVisible = false;
            break;
          } else {
            // find next anchor component
            const indexNextAnchorComponents = componentsJssMain.findIndex(
              (component) => component.uid === anchorComponents[index + 1]?.uid
            );
            // if there are no others anchor component, then check the components until the end of length for componentJssMain
            const checknComponentsBetween = isEditorial
              ? indexNextAnchorComponents > index && indexNextAnchorComponents !== -1
              : indexNextAnchorComponents !== -1;
            const nComponentsBetween = checknComponentsBetween ? indexNextAnchorComponents - i - 1 : componentsJssMain.length - i - 1;
            tempTab.isVisible = false;
            for (let j = i + 1; j <= i + nComponentsBetween; j++) {
              // if true there is at least one component to show, and so exit from the loop
              const checkComponent = isEditorial ? componentsJssMain[j].componentName : isEmptyResponse(componentsJssMain[j]);
              if (checkComponent) {
                tempTab.isVisible = true;
                break;
              }
            }
            if (tempTab.isVisible) break;
          }
        }
      }
    }
    return tempTab;
  });
};
const NavigationSection: FC<WithTranslation & TNavigationBarProps> = (props) => {
  const { sticky, isEditorial, dropdownIsOpen, setshowPanel } = props;
  const model = useMemo(() => new NavigationBarModel(props), [props]);
  const componentsJssMain = props.sitecoreContext?.route?.placeholders['jss-main'];
  const isMobile = useMediaQuery(`(max-width: ${BreakpointsInPx.md})`);
  const showDropdown = model?.showDropdown;
  const showLabel = model?.showLabel;
  const dropDownSeriesData = model?.seriesList;
  const [tabs, setTabs] = useState<TTabsNavigationBar[] | null>(null);
  const [anchorComponents, setAnchorComponents] = useState<any>();
  const [activeTab, setActiveTab] = useState<number>(0);

  const handleClick = useCallback((): void => {
    dropDownSeriesData && dropDownSeriesData?.length > 1 && setshowPanel(!dropdownIsOpen);
  }, [setshowPanel, dropdownIsOpen]);

  const handleCloseDropdown = useCallback((): void => {
    setshowPanel(false);
  }, [setshowPanel]);

  const scrollToPlaceholder = (index): void => {
    if (isScrolling()) return;

    const offsetCorrection = isMobile ? 44 : 74;
    const topOffset = anchorComponents?.item(index)?.offsetTop - offsetCorrection;

    if (topOffset) {
      disableScroll();
      windowService().scroll({
        top: topOffset,
        behavior: 'smooth',
      });
    }
    enableScroll();
  };

  const detectActiveTab = useCallback(() => {
    if (!anchorComponents) return;
    const sectionHighlightTabHeight = windowService().innerHeight / 4;

    // if at least the first element is not in the highlighted section set the active tab to first
    if (anchorComponents.item(0)?.getBoundingClientRect()?.top > sectionHighlightTabHeight) {
      setActiveTab(0);
      return;
    }

    let activeTabDetect = 0;
    for (let i = 0; i < anchorComponents.length; i++) {
      if (anchorComponents?.item(i)?.getBoundingClientRect().top < sectionHighlightTabHeight) {
        activeTabDetect = i;
      } else {
        break;
      }
    }
    setActiveTab(activeTabDetect);
  }, [anchorComponents]);

  const clickPageNavEvent: TClickPageNavEvent = (type: string, action: string | undefined, elementNumber?: number | undefined) => {
    googleClickEvent(type, 'Page Nav', action, model.title, elementNumber);
  };

  // detect active tab
  useEffect(() => {
    try {
      windowService().addEventListener('scroll', detectActiveTab, { passive: true });
    } catch (e) {
      windowService().addEventListener('scroll', detectActiveTab);
    }
    return (): void => {
      windowService().removeEventListener('scroll', detectActiveTab);
    };
  }, [anchorComponents, detectActiveTab]);

  // show only the tab for the anchor component in page -> 3 anchor component = 3 tab
  useEffect(() => {
    if (model?.tabs?.length === 0) return;
    const anchorComponentsTab = (componentsJssMain as ComponentRendering[] ).filter((component) => component.componentName === ANCHOR_COMPONENT);
    if (anchorComponentsTab) {
      const anchorTabs = model?.tabs
        .filter((_, index) => index < anchorComponentsTab.length)
        .map((item) => {
          return {
            label: item,
            isVisible: true,
          };
        });

      const visibleTabs = hiddenTabsBasedOnContentAvailability(anchorTabs, componentsJssMain, anchorComponentsTab, isEditorial);
      setTabs(visibleTabs);
    }
  }, [componentsJssMain, isEditorial, model]);
  //retrieve the anchor components
  useEffect(() => {
    if (model?.tabs?.length === 0) return;
    if (!tabs) return;
    const components = documentService().querySelectorAll(`.${NAVIGATION_BAR_ANCHOR_CLASS}`);
    setAnchorComponents(components);
  }, [tabs, model]);

  useEffect(() => {
    const arr = documentService().getElementsByTagName('header');
    if (arr?.length) {
      const header = arr[0];
      const height = '85px';
      header.style.setProperty('--navigation--Height--actual', height);
    }
  }, [isMobile]);

  const formTitleUrl: TFormTitleUrl = (baseUrl: string, titleUrl: string, skipSeries = false) => {
    const urlArr = baseUrl.split('/');
    urlArr.pop();
    if (skipSeries) {
      urlArr.pop();
    }
    urlArr.push(titleUrl);
    return urlArr.join('/');
  };

  useEffect(() => {
    const handleClose = () => {
      setshowPanel(false);
    };
    if (dropdownIsOpen && !isMobile) {
      documentService()?.addEventListener('click', handleClose);
    }
    return () => {
      documentService()?.removeEventListener('click', handleClose);
    };
  }, [dropdownIsOpen]);

  if (isMobile) {
    return (
      <>
        {!sticky && (
          <NavigationBoxMobile
            title={model.title}
            sticky={sticky}
            button1={model.button1}
            clickPageNavEvent={clickPageNavEvent}
            handleClick={handleClick}
            dropdownIsOpen={dropdownIsOpen}
            handleCloseDropdown={handleCloseDropdown}
            dropDownSeriesData={dropDownSeriesData}
            formTitleUrl={formTitleUrl}
            contextItemUrl={model.contextItemUrl}
            isUnderSuperSerie={model.isUnderSuperSerie}
            enableBuildPriceCTA={model.enableBuildPriceCTA}
          />
        )}
        <NavigationTabsMobile
          sticky={sticky}
          activeTab={activeTab}
          tabs={tabs}
          isEditorial={isEditorial}
          scrollToPlaceholder={scrollToPlaceholder}
          clickPageNavEvent={clickPageNavEvent}
        />
      </>
    );
  }

  return (
    <div className={`navigation-bar ${sticky ? 'sticky-element--hide' : ''}`}>
      {!isEditorial && showDropdown && (
        <NavigationBarDropdownDesktop
          handleClick={handleClick}
          title={model.title}
          dropDownSeriesData={dropDownSeriesData}
          dropdownIsOpen={dropdownIsOpen}
          contextItemUrl={model.contextItemUrl}
          isUnderSuperSerie={model.isUnderSuperSerie}
          handleCloseDropdown={handleCloseDropdown}
          formTitleUrl={formTitleUrl}
          clickPageNavEvent={clickPageNavEvent}
        />
      )}
      {!!(isEditorial && model.editorialTitle) && (
        <Text field={model.editorialTitle} tag="h2" className="navigation-bar__editorial-title" />
      )}
      <NavigationBarTabsDesktop
        tabs={tabs}
        scrollToPlaceholder={scrollToPlaceholder}
        clickPageNavEvent={clickPageNavEvent}
        activeTab={activeTab}
        isEditorial={isEditorial}
      />
      {!isEditorial && showLabel && (
        <NavigationBarMSRPDesktop optionalLabel1={model.optionalLabel1} optionalLabel2={model.optionalLabel2} msrp={model.msrp} />
      )}
      {
        <NavigationBarCtaDesktop
          isEditorial={isEditorial}
          editorialCTA={model.editorialCTA}
          button1={model.button1}
          isMobile={isMobile}
          clickPageNavEvent={clickPageNavEvent}
          enableBuildPriceCTA={model.enableBuildPriceCTA}
        />
      }
    </div>
  );
};

export default React.memo(NavigationSection);
